import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Lock, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useFormat } from "../contexts/PrefsContext";

// Re-appears 6h after a snooze, so users can finish what they're doing but
// will be re-prompted within the same day if they linger without subscribing.
// There is no "hard block" any more — the app's philosophy is that a lapsed
// subscription enters Read-Only Mode, never a lockout. The persistent
// ReadOnlyBanner handles ongoing messaging; this modal is a one-time,
// dismissible welcome-to-read-only nudge.
const SNOOZE_KEY = "vu_trial_ended_snooze_until";
const SNOOZE_HOURS = 6;

export default function TrialEndedModal() {
    const { t } = useTranslation();
    const { fmtCurrency } = useFormat();
    const { user } = useAuth();
    const [show, setShow] = useState(false);
    const [bs, setBs] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user?.role) return;
        // Founders never see this — belt for the braces that /billing/status
        // already returns "active" for them.
        if (user.is_founder) return;
        api.get("/billing/status").then(({ data }) => {
            setBs(data);
            if (data.subscription_status === "active") return;
            const blocked = data.subscription_status === "cancelled" || (data.days_left || 0) === 0;
            if (!blocked) return;
            const snoozeUntil = +localStorage.getItem(SNOOZE_KEY) || 0;
            if (Date.now() < snoozeUntil) return;
            setShow(true);
        }).catch(() => {});
    }, [user?.user_id, user?.is_founder, user?.role]);

    if (!show || !bs) return null;

    const snooze = () => {
        localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_HOURS * 3600 * 1000));
        setShow(false);
    };

    const subscribe = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error("Could not start checkout", {
                description: e.response?.data?.detail || e.message,
            });
            setBusy(false);
        }
    };

    const cancelled = bs.subscription_status === "cancelled";
    const planLabel = bs.plan_label || (user.role === "student" ? "Student" : "Professional");
    // Stripe charges in GBP — pin the currency to GBP but use the shared
    // formatter so digit grouping follows the user's locale preference.
    const priceStr = fmtCurrency(bs.plan_price_gbp || 0, "GBP");

    return (
        <div className="fixed inset-0 z-[65] bg-black/55 grid place-items-center p-4" data-testid="trial-ended-modal">
            <div className="card-warm w-full max-w-md p-6 relative border-l-4" style={{ borderLeftColor: "var(--brand-purple)" }}>
                <button onClick={snooze} aria-label={t("common.close")}
                        className="absolute top-3 right-3 text-[var(--text-ink)]/55 hover:text-[var(--brand-purple)]"
                        data-testid="trial-ended-snooze">
                    <X size={18} />
                </button>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full sparkle-badge text-xs mb-3">
                    <Lock size={12} /> {cancelled ? t("trial.endedBadgeCancelled") : t("trial.endedBadgeEnded")}
                </div>
                <div className="font-serif text-2xl">
                    {cancelled ? t("trial.endedCancelledTitle") : t("trial.endedTitle")}
                </div>
                <div className="text-sm text-[var(--text-ink)]/75 mt-2 leading-relaxed">
                    <strong>{t("trial.endedWorkspaceSafe")}</strong> {t("trial.endedBody")}
                </div>
                <ul className="text-sm mt-4 space-y-1.5 text-[var(--text-ink)]/85">
                    {[
                        t("trial.feats.meeting"),
                        t("trial.feats.memory"),
                        t("trial.feats.studyhub"),
                        t("trial.feats.missed"),
                        t("trial.feats.ref"),
                    ].map((f) => (
                        <li key={f} className="flex items-start gap-2">
                            <Sparkles size={12} className="text-[var(--brand-orange)] mt-1 shrink-0" /> {f}
                        </li>
                    ))}
                </ul>

                <div className="card-warm mt-5 p-4 flex items-baseline justify-between" data-testid="trial-ended-plan">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--text-ink)]/55">{t("trial.endedYourPlan")}</div>
                        <div className="font-serif text-xl">{planLabel}</div>
                    </div>
                    <div className="text-right">
                        <div className="font-serif text-3xl" style={{ color: "var(--brand-purple)" }} data-testid="trial-ended-price">{priceStr}</div>
                        <div className="text-[10px] text-[var(--text-ink)]/55">{t("trial.endedPerMonth")}</div>
                    </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2 justify-end">
                    <button onClick={snooze} className="btn-ghost text-sm" data-testid="trial-ended-later">
                        {t("trial.endedBrowse")}
                    </button>
                    <button onClick={subscribe} disabled={busy}
                            data-testid="trial-ended-subscribe"
                            className="btn-primary text-sm inline-flex items-center gap-2">
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {busy ? t("trial.endedRedirecting") : t("trial.endedSubscribe")}
                    </button>
                </div>
                <div className="text-[10px] text-[var(--text-ink)]/50 mt-3 text-center">
                    {t("trial.endedFooter")}
                </div>
            </div>
        </div>
    );
}
